/**
 * De zwevende toelichting bij wat je aanwijst.
 *
 * Het origineel heeft die overal: hou je de muis boven een stapel, dan staat er
 * "Uitbouwkaartenstapel"; boven een dorp "Dorp - 1 overwinningspunt". Het is een klein
 * wit kadertje met zwarte tekst en een dunne rand, vlak naast de muis — zie
 * `catan-ref/shots/ref/30-stapelbrowser.png` en `15-landschap-geplaatst.png`.
 *
 * Er is er altijd maar één tegelijk, dus hij woont hier als één gedeelde laag in plaats
 * van als een sprite per element. Elementen melden zich aan met `volgTooltip()`.
 */
import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { RASTER_BREED, RASTER_HOOG } from './skin.js';

const RAND = 4;
const LETTERMAAT = 12;

/** De laag waarin de toelichting tekent; hangt bovenaan, over alles heen. */
export class Tooltips {
  readonly laag = new Container();
  private doos = new Container();
  private vlak = new Graphics();
  private label: Text;

  constructor() {
    this.label = new Text({
      text: '',
      style: new TextStyle({
        // Het origineel gebruikt hier een schreefloze systeemletter, niet de sierletter
        // van de knoppen — het is een toelichting, geen opschrift.
        fontFamily: 'Tahoma, "DejaVu Sans", sans-serif',
        fontSize: LETTERMAAT,
        fill: 0x000000,
      }),
    });
    this.doos.addChild(this.vlak, this.label);
    this.doos.visible = false;
    this.laag.addChild(this.doos);
    // De toelichting mag zelf nooit klikken opvangen; hij zweeft er alleen boven.
    this.laag.eventMode = 'none';
    this.doos.eventMode = 'none';
  }

  /** Toont de toelichting bij een punt in het raster. */
  toon(inhoud: string, x: number, y: number): void {
    if (inhoud === '') {
      this.verberg();
      return;
    }
    this.label.text = inhoud;
    const breed = this.label.width + RAND * 2;
    const hoog = this.label.height + RAND * 2;

    this.vlak
      .clear()
      .rect(0, 0, breed, hoog)
      .fill({ color: 0xffffff })
      .stroke({ color: 0x000000, width: 1 });
    this.label.x = RAND;
    this.label.y = RAND;

    // Naast de muis, maar nooit half buiten beeld.
    this.doos.x = Math.min(x + 14, RASTER_BREED - breed - 2);
    this.doos.y = Math.min(y + 18, RASTER_HOOG - hoog - 2);
    this.doos.visible = true;
  }

  verberg(): void {
    this.doos.visible = false;
  }
}

/**
 * Hangt een toelichting aan een element.
 *
 * `plek` levert het punt in rastercoördinaten waar het kadertje moet komen — de
 * aanroeper weet dat, want het bord tekent in een geschaalde wereld en de tooltip in het
 * vaste raster.
 */
export function volgTooltip(
  doel: Container,
  tooltips: Tooltips,
  inhoud: string | (() => string),
  plek: () => { x: number; y: number },
): void {
  doel.eventMode = 'static';
  doel.on('pointerover', () => {
    const punt = plek();
    tooltips.toon(typeof inhoud === 'string' ? inhoud : inhoud(), punt.x, punt.y);
  });
  doel.on('pointerout', () => tooltips.verberg());
  // Verdwijnt een element terwijl de muis erboven hangt, dan mag het kadertje niet
  // blijven staan.
  doel.on('destroyed', () => tooltips.verberg());
}
